import React from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Star, ExternalLink, ShoppingBag } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Component for displaying product cards
 */
export const ProductCard = ({ product }) => {
  const { t } = useTranslation();
  const {
    title,
    image,
    url,
    sku,
    brand,
    price,
    currency,
    rating,
    availability,
    snippet,
  } = product;

  const formatPrice = (price, currency) => {
    if (!price) return null;
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: currency || "EUR",
    }).format(price);
  };

  const renderRating = (rating) => {
    if (!rating) return null;

    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <Star
          key="half"
          className="w-3 h-3 fill-yellow-400 text-yellow-400 opacity-50"
        />
      );
    }

    return (
      <div className="flex items-center space-x-1">
        <div className="flex">{stars}</div>
        <span className="text-xs text-muted-foreground">({rating})</span>
      </div>
    );
  };

  const getAvailabilityColor = (availability) => {
    switch (availability?.toLowerCase()) {
      case "instock":
        return "bg-green-100 text-green-800";
      case "outofstock":
        return "bg-red-100 text-red-800";
      case "preorder":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatAvailability = (availability) => {
    const map = {
      instock: t("components.productCards.inStock"),
      outofstock: t("components.productCards.outOfStock"),
      preorder: t("components.productCards.preOrder"),
    };
    return map[availability?.toLowerCase()] || availability;
  };

  return (
    <Card className="hover:shadow-lg transition-shadow duration-200">
      <CardContent className="p-4">
        <div className="flex space-x-4">
          {/* Product Image */}
          {image && (
            <div className="flex-shrink-0">
              <img
                src={image}
                alt={title}
                className="w-16 h-16 object-cover rounded-md"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
          )}

          {/* Product Info */}
          <div className="flex-1 min-w-0 space-y-2">
            {/* Title & Brand */}
            <div>
              <h3 className="font-medium text-sm leading-tight line-clamp-2">
                {url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {title}
                    <ExternalLink className="inline w-3 h-3 ml-1" />
                  </a>
                ) : (
                  title
                )}
              </h3>
              {brand && (
                <p className="text-xs text-muted-foreground mt-1">{brand}</p>
              )}
            </div>

            {/* Rating */}
            {rating && (
              <div className="flex items-center">{renderRating(rating)}</div>
            )}

            {/* Snippet */}
            {snippet && (
              <p className="text-xs text-muted-foreground line-clamp-2">
                {snippet}
              </p>
            )}

            {/* Bottom Row: Price, SKU, Availability */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {price && (
                  <span className="font-semibold text-lg text-green-600">
                    {formatPrice(price, currency)}
                  </span>
                )}
                {sku && (
                  <Badge variant="secondary" className="text-xs">
                    {sku}
                  </Badge>
                )}
              </div>

              <div className="flex items-center space-x-2">
                {availability && (
                  <Badge
                    className={`text-xs ${getAvailabilityColor(availability)}`}
                  >
                    {formatAvailability(availability)}
                  </Badge>
                )}
                <ShoppingBag className="w-4 h-4 text-muted-foreground" />
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Component for displaying a grid of product cards
 */
export const ProductCardsGrid = ({ cards, title }) => {
  const { t } = useTranslation();
  if (!cards || cards.length === 0) return null;

  debugger;
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold flex items-center">
        <ShoppingBag className="w-5 h-5 mr-2" />
        {title || t("components.productCards.productsFound")}
        <Badge variant="secondary" className="ml-2">
          {cards.length}
        </Badge>
      </h3>
      <div className="grid gap-3">
        {cards.map((card, index) => (
          <ProductCard key={card.sku || card.url || index} product={card} />
        ))}
      </div>
    </div>
  );
};
